/** Angular */
import { Injectable } from '@angular/core';
/** Rxjs */
import { Observable } from 'rxjs';
/** Models */
import { DsCompanyGroup } from '../models/ds-company-group';
/** Interfaces */
import { DsCompanyGroupUserLink } from '../interfaces/ds-company-group-user-link.interface';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** Sixense */
import { BydModel, BydHttpParams } from '@sixense/byd-core-service';

@Injectable({
  providedIn: 'root',
})
export class DsCompanyGroupsService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path = '';

  /**
   * The endpoint base
   */
  private basePath = this.bydFeatureFlagService.hasFlags('dscompany_path')
    ? '/dscompany/v1/projects/'
    : '/v1/projects/';

  /**
   * Get all groups of company.
   *
   * @param projectId Selected project id
   * @param companyId Selected company id
   * @returns Observable<BydModel<DsCompanyGroup>>
   */
  public getCompanyGroups(projectId: number, companyId: number): Observable<BydModel<DsCompanyGroup>> {
    this.path = `${this.basePath}${projectId}/companies/${companyId}/groups`;
    return this.getModels<DsCompanyGroup>();
  }

  /**
   * Add user to company.
   *
   * @param projectId Selected project id
   * @param userId Selected user id
   * @param companyId Selected company id
   * @returns boolean.
   */
  public postCompanyUser(projectId: number, userId: number, companyId: number): Observable<boolean> {
    this.path = `${this.basePath}${projectId}/companies/${companyId}/users/${userId}`;
    return this.post<boolean>();
  }

  /**
   * Delete user from company.
   *
   * @param projectId Selected project id
   * @param userId Selected user id
   * @param companyId Selected company id
   * @returns boolean.
   */
  public deleteCompanyUser(projectId: number, userId: number, companyId: number): Observable<boolean> {
    this.path = `${this.basePath}${projectId}/companies/${companyId}/users/${userId}`;
    return this.delete<boolean>();
  }

  /**
   * Get groups with a specific right using pagination, or all groups without distinction of group.
   *
   * @param projectId the project id
   * @param pageIndex the page index
   * @param elementsPerPage the element number per page
   * @param right the groups right
   * @param companyId then company id
   * @returns Observable<BydModel<DsCompanyGroup>>
   */
  public getGroups(
    projectId: number,
    pageIndex = 0,
    elementsPerPage = 10,
    right: '' | 'pin' | 'doc'
  ): Observable<BydModel<DsCompanyGroup>> {
    this.path = `${this.basePath}${projectId}/companies/groups`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    const params = right ? new BydHttpParams().set('right', right) : undefined;
    return this.getModels<DsCompanyGroup>({ headers, params });
  }

  /**
   * Get all links between company groups and users.
   *
   * @param projectId Selected project id
   * @param companyId Selected company id
   * @returns Observable<DsCompanyGroupUserLink[]>
   */
  public getGroupUserLinks(projectId: number, companyId: number): Observable<DsCompanyGroupUserLink[]> {
    this.path = `${this.basePath}${projectId}/companies/${companyId}/groups/users`;
    return this.get<DsCompanyGroupUserLink[]>();
  }

  /**
   * Adds a link between a company user group and a user.
   *
   * @param projectId Selected project id
   * @param companyGroupId Selected company group id
   * @param userId Selected user id
   * @returns  Observable<number>
   */
  public addUserLink(projectId: number, companyGroupId: number, userId: number): Observable<number> {
    this.path = `${this.basePath}${projectId}/companyusergroups/${companyGroupId}/users/${userId}`;
    return this.post<number>();
  }

  /**
   * Deletes a link between a company user group and a user.
   *
   * @param projectId Selected project id
   * @param companyGroupId Selected company group id
   * @param userId Selected user id
   * @returns Observable<boolean>
   */
  public deleteUserLink(projectId: number, companyGroupId: number, userId: number): Observable<boolean> {
    this.path = `${this.basePath}${projectId}/companyusergroups/${companyGroupId}/users/${userId}`;
    return this.delete<boolean>();
  }

  /**
   * Create DsCompany instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsCompanyGroup` instance.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public createNewInstance(jsonObject: any): DsCompanyGroup {
    return new DsCompanyGroup(jsonObject);
  }
}
